import { useEffect, useState } from "react";
import { Form, Button, Container, Row, Col, Table, Card } from "react-bootstrap";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../styles/orderForm.css";

const OrderForm = () => {
  const [item, setItem] = useState({ breadType: "", size: "", quantity: 1 });
  const [orderItems, setOrderItems] = useState([]);
  const [note, setNote] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState(null);
  const [breadOptions, setBreadOptions] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [priceMap, setPriceMap] = useState({});
  const [sizeMap, setSizeMap] = useState({});

  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchBreadOptions = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const res = await axios.get(`${API_BASE}/api/customers/items`, { headers });
        const data = res.data;

        const types = data.map(item => item.bread_type);
        setBreadOptions(types);

        const priceLookup = {};
        const newSizeMap = {};

        data.forEach(item => {
          const breadType = item.bread_type;
          const sizes = item.sizes;

          newSizeMap[breadType] = Object.keys(sizes);

          Object.entries(sizes).forEach(([size, price]) => {
            priceLookup[`${breadType}_${size}`] = price;
          });
        });

        setPriceMap(priceLookup);
        setSizeMap(newSizeMap);

        const defaultBread = types[0] || "white";
        const defaultSizes = newSizeMap[defaultBread] || [];

        setItem({
          breadType: defaultBread,
          size: defaultSizes[0] || "",
          quantity: 1
        });

        setAvailableSizes(defaultSizes);
      } catch (err) {
        console.error("Failed to fetch bread items:", err);
      }
    };

    fetchBreadOptions();
  }, [API_BASE]);

  useEffect(() => {
    if (item.breadType && sizeMap[item.breadType]) {
      const sizes = sizeMap[item.breadType];
      setAvailableSizes(sizes);
      setItem(prev => ({ ...prev, size: sizes[0] || "" }));
    }
  }, [item.breadType, sizeMap]);

  const handleChange = (e) => {
    setItem({ ...item, [e.target.name]: e.target.value });
  };

  const handleAddOrUpdateItem = () => {
    const unit_price = priceMap[`${item.breadType}_${item.size}`] || 0;
    const itemWithPrice = {
      breadType: item.breadType,
      size: item.size,
      quantity: item.quantity,
      unit_price
    };

    if (editIndex !== null) {
      const updatedItems = [...orderItems];
      updatedItems[editIndex] = itemWithPrice;
      setOrderItems(updatedItems);
      setEditIndex(null);
    } else {
      setOrderItems([...orderItems, itemWithPrice]);
    }

    const firstBread = breadOptions[0] || "white";
    const firstSize = sizeMap[firstBread]?.[0] || "";

    setItem({ breadType: firstBread, size: firstSize, quantity: 1 });
  };

  const handleEdit = (index) => {
    setItem(orderItems[index]);
    setEditIndex(index);
  };

  const handleDelete = (index) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
    if (editIndex === index) {
      const firstBread = breadOptions[0] || "white";
      const firstSize = sizeMap[firstBread]?.[0] || "";

      setItem({ breadType: firstBread, size: firstSize, quantity: 1 });
      setEditIndex(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (orderItems.length === 0) {
      setFormError("❗ Please add at least one item before submitting the order.");
      return;
    }

    const customer_id = localStorage.getItem("customer_id");
    const fullOrder = {
      items: orderItems.map(item => ({
        bread_type: item.breadType, 
        size: parseFloat(item.size),//size_inch
        quantity: item.quantity,
        unit_price: item.unit_price
      })),
      order_status: "processing",
      order_date: new Date().toISOString().split("T")[0],
      delivery_date: new Date().toISOString().split("T")[0],
      payment_status: "unpaid",
      payment_method: "cash",
      customer_note: note,
      customer_id
    };

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      await axios.post(`${API_BASE}/api/orders/add`, fullOrder, { headers });

      setSuccessMessage("Order submitted successfully! Redirecting...");
      setOrderItems([]);
      setNote("");
       setTimeout(() => navigate("/customer/ManageOrder"), 1000);

    } catch (err) {
      console.error("Error submitting order:", err);
      setFormError("Failed to submit order. Please try again.");
    }
  };

  const calculateTotal = () =>
    orderItems.reduce((total, item) => total + item.unit_price * item.quantity, 0).toFixed(2);

  return (
    <Container className="order-form-container">
      <h2 className="text-center mb-4">Place Your Order</h2>

      {successMessage && (
        <Card className="mb-3">
          <Card.Body className="text-success fw-bold">{successMessage}</Card.Body>
        </Card>
      )}

      <Form onSubmit={handleSubmit} className="order-form">
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>Bread Type</Form.Label>
          <Col sm={9}>
            <Form.Select name="breadType" value={item.breadType} onChange={handleChange}>
              {breadOptions.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </Form.Select>
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>Size (inches)</Form.Label>
          <Col sm={9}>
            <Form.Select name="size" value={item.size} onChange={handleChange}>
              {availableSizes
                .sort((a, b) => parseFloat(a) - parseFloat(b))
                .map(size => (
                  <option key={size} value={size}>{size}"</option>
                ))}
            </Form.Select>
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>Quantity</Form.Label>
          <Col sm={9}>
            <Form.Control
              type="number"
              name="quantity"
              value={item.quantity}
              min="1"
              onChange={handleChange}
            />
          </Col>
        </Form.Group>

        <Row className="mb-4">
          <Col sm={{ span: 9, offset: 3 }}>
            <Button
              variant={editIndex !== null ? "warning" : "success"}
              type="button"
              onClick={handleAddOrUpdateItem}
              className="w-100"
            >
              {editIndex !== null ? "Update Item" : "Add Item to Order"}
            </Button>
          </Col>
        </Row>

        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>Note</Form.Label>
          <Col sm={9}>
            <Form.Control
              as="textarea"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Any special requests?"
            />
          </Col>
        </Form.Group>

        {orderItems.length > 0 && (
          <>
            <h5 className="mt-4">Order Summary</h5>
            <Table bordered hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Bread</th>
                  <th>Size</th>
                  <th>Qty</th>
                  <th>Unit Price</th>
                  <th>Total</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orderItems.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.breadType}</td>
                    <td>{item.size}"</td>
                    <td>{item.quantity}</td>
                    <td>${item.unit_price.toFixed(2)}</td>
                    <td>${(item.unit_price * item.quantity).toFixed(2)}</td>
                    <td>
                      <Button variant="info" size="sm" onClick={() => handleEdit(index)} className="me-2">Edit</Button>
                      <Button variant="danger" size="sm" onClick={() => handleDelete(index)}>Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="5" className="text-end fw-bold">Total:</td>
                  <td colSpan="2" className="fw-bold">${calculateTotal()}</td>
                </tr>
              </tfoot>
            </Table>
          </>
        )}

        <Form.Group as={Row} className="mb-3">
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" type="submit" className="w-100" disabled={orderItems.length === 0}>
              Submit Order
            </Button>
            {formError && (
              <div className="text-danger mt-2 fw-bold">
                {formError}
              </div>
            )}
          </Col>
        </Form.Group>
      </Form>
    </Container>
  );
};

export default OrderForm;
